import { DeleteOutlined } from '@ant-design/icons';
import { Button, Input, Popconfirm } from 'antd';
import cn from 'classnames';

import type { LeadCardHistoryItemData } from '@app/components/LeadCard/data';
import { formatLeadHistoryTimestamp } from '@app/components/LeadCard/ui/MessageHistory/lib';

import { useLeadCommentMessage } from '../hooks/useLeadCommentMessage';

import type { FC } from 'react';

import './LeadCommentMessage.scss';

interface LeadCommentMessageProps {
  item: LeadCardHistoryItemData;
  // Закреплённый комментарий старше загруженной истории: стоит в стопке над
  // лентой, а не в потоке (issue/11369).
  isDetached?: boolean;
}

export const LeadCommentMessage: FC<LeadCommentMessageProps> = ({
  item,
  isDetached = false,
}) => {
  const commentText = item.ActionItems?.[0]?.ValueNew ?? '';

  const {
    rootRef,
    textRef,
    isPinned,
    isUnpinning,
    unpin,
    stickyOffset,
    isExpanded,
    isExpandable,
    toggleExpanded,
    collapsedMaxHeight,
    expandedMaxHeight,
    isEditing,
    isSaving,
    isDeleting,
    draftText,
    setDraftText,
    startEditing,
    saveDraft,
    deleteComment,
  } = useLeadCommentMessage({
    commentId: item.ObjectId,
    commentText,
  });

  const textMaxHeight = isExpanded ? expandedMaxHeight : collapsedMaxHeight;

  return (
    <div
      ref={rootRef}
      className={cn('LeadCommentMessage', {
        'LeadCommentMessage--sticky': isPinned,
        'LeadCommentMessage--detached': isPinned && isDetached,
      })}
      style={isPinned ? { top: stickyOffset } : undefined}
      data-action-id={item.IdS ?? ''}
    >
      <div className="LeadCommentMessage__header">
        {item.ActionUser && (
          <span className="LeadCommentMessage__author">{item.ActionUser}</span>
        )}
        <span className="LeadCommentMessage__timestamp">
          {formatLeadHistoryTimestamp(item.Timestamp)}
        </span>
        {isPinned && (
          <button
            type="button"
            className="LeadCommentMessage__unpin"
            disabled={isUnpinning}
            onClick={() => void unpin()}
          >
            Открепить
          </button>
        )}
      </div>

      {isEditing ? (
        <div className="LeadCommentMessage__editor">
          <Input.TextArea
            className="LeadCommentMessage__editor-input"
            value={draftText}
            onChange={({ target: { value } }) => setDraftText(value)}
            autoSize={{ minRows: 2, maxRows: 8 }}
            disabled={isSaving || isDeleting}
            autoFocus
          />
          <div className="LeadCommentMessage__editor-actions">
            <Button
              type="primary"
              size="small"
              className="LeadCommentMessage__editor-save"
              loading={isSaving}
              disabled={isDeleting}
              onClick={() => void saveDraft()}
            >
              Сохранить
            </Button>
            <Popconfirm
              title="Удалить комментарий?"
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true, loading: isDeleting }}
              onConfirm={() => void deleteComment()}
            >
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                className="LeadCommentMessage__editor-delete"
                loading={isDeleting}
                disabled={isSaving}
                aria-label="Удалить комментарий"
              />
            </Popconfirm>
          </div>
        </div>
      ) : (
        <>
          {/* Обёртка нужна ради градиента обрезки: сам текст — элемент с
              overflow: hidden, его собственный ::after прокрутился бы вместе
              с содержимым. */}
          <div
            className={cn('LeadCommentMessage__text-wrap', {
              'LeadCommentMessage__text-wrap--faded':
                isExpandable && !isExpanded,
            })}
          >
            <button
              ref={textRef}
              type="button"
              className={cn('LeadCommentMessage__text', {
                'LeadCommentMessage__text--expanded': isExpanded,
              })}
              style={{ maxHeight: textMaxHeight }}
              onClick={startEditing}
            >
              {commentText}
            </button>
          </div>
          {isExpandable && (
            <div className="LeadCommentMessage__footer">
              <button
                type="button"
                className="LeadCommentMessage__toggle"
                onClick={toggleExpanded}
              >
                {isExpanded ? 'Свернуть' : 'Показать полностью'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
